/**
 * Shared reads for `private_lesson_duration_options` — the per-program list of
 * bookable lesson lengths (15–120 min in 5-minute steps) with per-length price
 * and credit cost. Every surface that needs "which durations does this program
 * offer" (booking validation, slot generation, program form, credit math)
 * should read through here rather than re-querying ad hoc, so ordering and
 * active-filtering stay identical everywhere. Pricing itself resolves through
 * `resolveCurrentLessonPrice` (which layers coach overrides on top).
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { toStoredPrice } from '@/utils/billing/storedPrice';

export interface PrivateLessonDurationOption {
  id: string;
  program_id: string;
  duration_minutes: number;
  /** Dollars. */
  price: number;
  /** Lesson-pack credits one booking of this length consumes. */
  credit_cost: number;
  is_active: boolean;
}

/**
 * A program's ACTIVE duration options, minutes ascending (the display order).
 * Throws on a real read error — callers gating a charge must not treat an
 * unreadable option list as "no durations" (#961 fail-hard pattern).
 */
export async function fetchActiveDurationOptions(
  supabase: SupabaseClient<any, any, any>,
  programId: string,
): Promise<PrivateLessonDurationOption[]> {
  const { data, error } = await supabase
    .from('private_lesson_duration_options')
    .select('id, program_id, duration_minutes, price, credit_cost, is_active')
    .eq('program_id', programId)
    .eq('is_active', true)
    .order('duration_minutes', { ascending: true });

  if (error) {
    throw new Error(`Failed to read this program's lesson lengths: ${error.message}`);
  }

  return (data ?? []).map((row: any) => ({
    id: String(row.id),
    program_id: String(row.program_id),
    duration_minutes: Number(row.duration_minutes),
    price: Number.parseFloat(String(row.price ?? '0')) || 0,
    credit_cost: Number.parseFloat(String(row.credit_cost ?? '0')) || 0,
    is_active: row.is_active === true,
  }));
}

/**
 * A duration choice for pickers/estimates, derived from option rows embedded
 * in a program payload (staff GET nests them as `duration_options`; the
 * public program detail route returns the same field name).
 */
export interface DurationChoice {
  duration_minutes: number;
  /** Dollars — the program default for this length (coach overrides layer on top server-side). */
  price: number;
  credit_cost: number;
}

/**
 * Pure client-safe read of a program record's embedded duration option rows:
 * active rows only, minutes ascending. When the payload predates the option
 * rows (`duration_options` missing entirely), falls back to the legacy 30/60
 * flags so an un-refetched program object still renders usable choices. An
 * empty array is a real answer (no active lengths) — no fallback.
 */
export function activeDurationChoicesFromProgram(privateLessonRecord: {
  duration_options?: Array<{
    duration_minutes: number | string;
    price?: number | string | null;
    credit_cost?: number | string | null;
    is_active?: boolean | null;
  }> | null;
  session_duration_30min?: boolean | null;
  session_duration_60min?: boolean | null;
  price_30min?: number | string | null;
  price_60min?: number | string | null;
} | null | undefined): DurationChoice[] {
  if (!privateLessonRecord) return [];

  const rows = privateLessonRecord.duration_options;
  if (Array.isArray(rows)) {
    return rows
      .filter((row) => row && row.is_active !== false)
      .map((row) => ({
        duration_minutes: Number(row.duration_minutes),
        price: Number.parseFloat(String(row.price ?? '0')) || 0,
        credit_cost: Number.parseFloat(String(row.credit_cost ?? '0')) || 0,
      }))
      .filter((row) => Number.isFinite(row.duration_minutes) && row.duration_minutes > 0)
      .sort((a, b) => a.duration_minutes - b.duration_minutes);
  }

  // Legacy-flag fallback for payloads that don't carry option rows yet.
  const legacy: DurationChoice[] = [];
  if (privateLessonRecord.session_duration_30min) {
    legacy.push({
      duration_minutes: 30,
      price: Number.parseFloat(String(privateLessonRecord.price_30min ?? '0')) || 0,
      credit_cost: 0.5,
    });
  }
  if (privateLessonRecord.session_duration_60min) {
    legacy.push({
      duration_minutes: 60,
      price: Number.parseFloat(String(privateLessonRecord.price_60min ?? '0')) || 0,
      credit_cost: 1.0,
    });
  }
  return legacy;
}

// ---------------------------------------------------------------------------
// Duration option editing rules — shared by the program form (client) and the
// program API routes (server) so the form is never the only gate.
// ---------------------------------------------------------------------------

export const MIN_LESSON_DURATION_MINUTES = 15;
export const MAX_LESSON_DURATION_MINUTES = 120;
export const LESSON_DURATION_STEP_MINUTES = 5;
/** Server-side sanity cap on a single lesson's price (dollars). */
export const MAX_LESSON_PRICE_DOLLARS = 10000;
/** Server-side sanity cap on a single lesson's credit cost. */
export const MAX_LESSON_CREDIT_COST = 10;

/** All pickable lesson lengths, minutes ascending (15, 20, … 120). */
export function allowedLessonDurations(): number[] {
  const durations: number[] = [];
  for (
    let minutes = MIN_LESSON_DURATION_MINUTES;
    minutes <= MAX_LESSON_DURATION_MINUTES;
    minutes += LESSON_DURATION_STEP_MINUTES
  ) {
    durations.push(minutes);
  }
  return durations;
}

export function isValidLessonDuration(minutes: unknown): boolean {
  const value = Number(minutes);
  return (
    Number.isInteger(value)
    && value >= MIN_LESSON_DURATION_MINUTES
    && value <= MAX_LESSON_DURATION_MINUTES
    && value % LESSON_DURATION_STEP_MINUTES === 0
  );
}

/**
 * Default credit cost for a lesson length: duration ÷ 60, rounded to the
 * nearest 0.25, never below 0.25 (the DB requires credit_cost > 0).
 * 25 min → ½ credit, 45 min → ¾, 50 min → ¾, 60 → 1, 90 → 1½.
 */
export function defaultCreditCostForDuration(durationMinutes: number): number {
  const raw = Number(durationMinutes) / 60;
  const rounded = Math.round(raw / 0.25) * 0.25;
  return Math.max(0.25, rounded);
}

const QUARTER_FRACTIONS: Record<number, string> = { 25: '¼', 50: '½', 75: '¾' };

/**
 * Plain-English credit amount with natural fractions: 0.5 → "½ credit",
 * 1 → "1 credit", 1.25 → "1¼ credits", 0.6 → "0.6 credits".
 */
export function formatCreditCostText(creditCost: number): string {
  const value = Number(creditCost);
  if (!Number.isFinite(value) || value <= 0) return '0 credits';

  const whole = Math.floor(value);
  const centsFraction = Math.round((value - whole) * 100);
  const isQuarterStep = centsFraction === 0 || centsFraction === 25 || centsFraction === 50 || centsFraction === 75;

  let amount: string;
  if (centsFraction === 0) {
    amount = String(whole);
  } else if (isQuarterStep) {
    const fraction = QUARTER_FRACTIONS[centsFraction];
    amount = whole > 0 ? `${whole}${fraction}` : fraction;
  } else {
    amount = value.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
  }

  // "½ credit", "1 credit"; plural only above one (decimals stay plural).
  const singular = isQuarterStep && value <= 1;
  return `${amount} ${singular ? 'credit' : 'credits'}`;
}

/** A duration option row as edited/submitted (id present = already saved). */
export interface DurationOptionInput {
  id?: string | null;
  duration_minutes: number;
  price: number;
  credit_cost: number;
  is_active: boolean;
}

export type DurationOptionValidation =
  | { ok: true }
  | { ok: false; message: string };

/**
 * Validates a full submitted option list. Shared by the program form and the
 * program API routes so a direct API POST/PUT hits the same rules:
 * duration in range/step, price from $0 (a free lesson, #4968) up to the cap,
 * credit cost > 0 and ≤ 10, no duplicate durations, and at least one active
 * row. Messages are plain-English and safe to show to gym staff.
 */
export function validateDurationOptionRows(rows: unknown): DurationOptionValidation {
  if (!Array.isArray(rows) || rows.length === 0) {
    return { ok: false, message: 'Add at least one lesson length so families can book.' };
  }

  const seenDurations = new Set<number>();
  let activeCount = 0;

  for (const raw of rows as Array<Partial<DurationOptionInput>>) {
    const duration = Number(raw?.duration_minutes);
    if (!isValidLessonDuration(duration)) {
      return {
        ok: false,
        message: `Lesson lengths must be between ${MIN_LESSON_DURATION_MINUTES} and ${MAX_LESSON_DURATION_MINUTES} minutes, in ${LESSON_DURATION_STEP_MINUTES}-minute steps.`,
      };
    }
    if (seenDurations.has(duration)) {
      return { ok: false, message: `You have ${duration}-minute lessons listed more than once. Each lesson length can only appear once.` };
    }
    seenDurations.add(duration);

    const isActive = raw?.is_active !== false;
    // A missing price is refused, never read as $0: `Number(null)` and `Number('')` are 0.
    const price = toStoredPrice(raw?.price);
    if (price === null || price < 0 || price > MAX_LESSON_PRICE_DOLLARS) {
      return { ok: false, message: `Each lesson length needs a price between $0 and $${MAX_LESSON_PRICE_DOLLARS.toLocaleString()}.` };
    }

    const creditCost = Number(raw?.credit_cost);
    if (!Number.isFinite(creditCost) || creditCost <= 0 || creditCost > MAX_LESSON_CREDIT_COST) {
      return { ok: false, message: `The credit cost for ${duration}-minute lessons must be greater than 0 and no more than ${MAX_LESSON_CREDIT_COST}.` };
    }

    if (isActive) activeCount += 1;
  }

  if (activeCount === 0) {
    return { ok: false, message: 'Keep at least one lesson length active so families can book.' };
  }

  return { ok: true };
}

/**
 * Lesson credit packages (`private_lessons.package_pricing`). Unlike a lesson
 * length, a pack can never be free (#4968 R14): a $0 pack would hand out
 * credits for nothing. Shared by the program form and both program routes.
 */
export function validatePackagePricingRows(packages: unknown): DurationOptionValidation {
  if (packages == null) return { ok: true };
  if (!Array.isArray(packages)) {
    return { ok: false, message: 'Lesson credit packages could not be read. Please try again.' };
  }
  for (const pkg of packages as Array<{ price?: unknown }>) {
    const price = toStoredPrice(pkg?.price);
    if (price === null || price <= 0) {
      return { ok: false, message: 'Each lesson credit package needs a price above $0.' };
    }
  }
  return { ok: true };
}

/**
 * Legacy write-through values derived from the option rows: the 30/60 flag is
 * true only when an ACTIVE option for that exact length exists, and the legacy
 * price mirrors that row's price (otherwise flag false, price 0). Keeps the
 * lagging staff-internal readers of session_duration_30min/60min +
 * price_30min/60min truthful for one release cycle.
 */
export function deriveLegacyDurationFields(rows: DurationOptionInput[]): {
  session_duration_30min: boolean;
  session_duration_60min: boolean;
  price_30min: number;
  price_60min: number;
} {
  const active30 = rows.find((row) => row.is_active !== false && Number(row.duration_minutes) === 30);
  const active60 = rows.find((row) => row.is_active !== false && Number(row.duration_minutes) === 60);
  return {
    session_duration_30min: Boolean(active30),
    session_duration_60min: Boolean(active60),
    price_30min: active30 ? Number(active30.price) || 0 : 0,
    price_60min: active60 ? Number(active60.price) || 0 : 0,
  };
}

/**
 * SERVER-side resolution of the credit-pack cost to SPEND when booking this
 * program at this length: the ACTIVE option row's credit_cost. The
 * duration-based default is a fallback only — a bookable duration always has
 * an active option row (the charge-path resolver refuses otherwise), so
 * callers should log when `fromOptionRow` is false. Never trust a
 * client-sent credit cost; resolve here with a service-role client.
 */
export async function resolveBookingCreditCost(
  supabase: SupabaseClient<any, any, any>,
  programId: string,
  durationMinutes: number,
): Promise<{ creditCost: number; fromOptionRow: boolean }> {
  const options = await fetchActiveDurationOptions(supabase, programId);
  const match = options.find((option) => option.duration_minutes === Number(durationMinutes));
  if (match && match.credit_cost > 0) {
    return { creditCost: match.credit_cost, fromOptionRow: true };
  }
  return {
    creditCost: defaultCreditCostForDuration(Number(durationMinutes) || 60),
    fromOptionRow: false,
  };
}

/**
 * Credit amount to RESTORE for a credit-paid booking on cancellation or an
 * Excused Absent: the `credits_spent` snapshot written when the credit was
 * spent — never a recomputed value, so a later edit to the option's credit
 * cost can't over- or under-refund. The legacy duration rule (30 min = ½
 * credit, anything else = 1) applies only when the snapshot is null; the
 * migration backfilled pre-feature credit-paid bookings, so callers should
 * log when `fromSnapshot` is false.
 */
export function creditRestoreAmountForBooking(booking: {
  credits_spent?: number | string | null;
  session_duration?: number | string | null;
}): { amount: number; fromSnapshot: boolean } {
  const snapshot = Number.parseFloat(String(booking?.credits_spent ?? ''));
  if (Number.isFinite(snapshot) && snapshot > 0) {
    return { amount: snapshot, fromSnapshot: true };
  }
  const duration = Number(booking?.session_duration) || 60;
  return { amount: duration === 30 ? 0.5 : 1.0, fromSnapshot: false };
}

/** True when the program currently offers this lesson length. */
export async function programOffersDuration(
  supabase: SupabaseClient<any, any, any>,
  programId: string,
  durationMinutes: number,
): Promise<boolean> {
  const options = await fetchActiveDurationOptions(supabase, programId);
  return options.some((option) => option.duration_minutes === Number(durationMinutes));
}
